type PatientData = Record<string, unknown>;
type Listener = () => void;

/**
 * Singleton cache for patient-scoped localStorage data.
 *
 * Provides efficient access to patient data by caching localStorage reads
 * and notifying subscribed components when data changes. Each patient's data
 * is stored separately under `metriport_patient_data:${patientId}` key.
 *
 * Benefits:
 * - Single localStorage read per patient (cached thereafter)
 * - Pub-sub pattern for reactivity (listeners notify components of changes)
 * - Cross-tab synchronization via storage events
 * - Support for multiple data namespaces (viewedRows, bookmarks, etc.)
 */
class PatientStorageCache {
  private cache = new Map<string, PatientData>();
  private listeners = new Map<string, Set<Listener>>();

  private load(patientId: string): PatientData {
    const cached = this.cache.get(patientId);
    if (cached) {
      return cached;
    }

    const key = `metriport_patient_data:${patientId}`;
    try {
      const stored = localStorage.getItem(key);
      const data = stored ? (JSON.parse(stored) as PatientData) : {};
      this.cache.set(patientId, data);
      return data;
    } catch (error) {
      console.error("Failed to load patient data from localStorage:", error);
      return {};
    }
  }

  get(patientId: string, namespace: string): unknown {
    return this.load(patientId)[namespace];
  }

  set(patientId: string, namespace: string, value: unknown): void {
    const data = this.load(patientId);
    data[namespace] = value;

    const key = `metriport_patient_data:${patientId}`;
    try {
      localStorage.setItem(key, JSON.stringify(data));

      // Notify all subscribers for this patient
      this.listeners.get(patientId)?.forEach(fn => fn());

      // Dispatch custom event for same-window sync
      window.dispatchEvent(new CustomEvent("patientStorageChanged", { detail: patientId }));
    } catch (error) {
      console.error("Failed to save patient data to localStorage:", error);
    }
  }

  subscribe(patientId: string, listener: Listener): () => void {
    if (!this.listeners.has(patientId)) {
      this.listeners.set(patientId, new Set());
    }
    const listenerSet = this.listeners.get(patientId);
    if (listenerSet) {
      listenerSet.add(listener);
    }

    // Return unsubscribe function
    return () => {
      this.listeners.get(patientId)?.delete(listener);
    };
  }

  clearCache(patientId: string): void {
    this.cache.delete(patientId);
  }
}

// Singleton instance
export const patientStorageCache = new PatientStorageCache();

// Cross-tab sync via storage events
if (typeof window !== "undefined") {
  window.addEventListener("storage", e => {
    if (e.key?.startsWith("metriport_patient_data:")) {
      const patientId = e.key.split(":")[1];
      if (patientId) {
        patientStorageCache.clearCache(patientId);
        // Notify all subscribers to re-render with fresh data
        patientStorageCache["listeners"].get(patientId)?.forEach(fn => fn());
      }
    }
  });

  // Same-window sync via custom events
  window.addEventListener("patientStorageChanged", (e: Event) => {
    const customEvent = e as CustomEvent;
    const patientId = customEvent.detail as string;
    if (patientId) {
      patientStorageCache["listeners"].get(patientId)?.forEach(fn => fn());
    }
  });
}
